import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { ButtonTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MESSAGE_TEMPLATES_URL } from "@constants/routes";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { FormattedMessageTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/message-template";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { navigateToUtil } from "@utils/navigation-util";
import { getMessageTemplateValidationErrors } from "@utils/validation/message-template/message-template-validation-util";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { MessageTemplateGeneralInfoTab } from "@containers/message-template-details/message-template-details-component/message-template-general-info-tab/message-template-general-info-tab";
import { MessageTemplateContentTab } from "@containers/message-template-details/message-template-details-component/message-template-content-tab/message-template-content-tab";
import styles from "./message-template-details-component.less";
import { IDWSession } from "@idw-types/data/idw-session";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import isEqual from "lodash/isEqual";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { ModalTypes } from "@enums/modal-types";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { useModalContext } from "@context/use-modal-context";
import { MessageTemplateDetailsTabTypes } from "@enums/message-template-details-tab-types";

type MessageTemplateDetailsComponentProps = {
    session: IDWSession;
    initialMessageTemplate: FormattedMessageTemplate;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    handleSave: (messageTemplateState: FormattedMessageTemplate) => Promise<FormattedMessageTemplate>;
};

export const MessageTemplateDetailsComponent = (props: MessageTemplateDetailsComponentProps) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { session, initialMessageTemplate, axnCompanies, selfServiceVerificationWhiteLabeledEmails, handleSave } = props;
    const [messageTemplateState, setMessageTemplateState] = useState<FormattedMessageTemplate>(initialMessageTemplate);
    const modalContext = useModalContext();

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [, setCurrentTab] = useState(MessageTemplateDetailsTabTypes.GENERAL_INFO);
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const resource = resources.pages.messageTemplateDetails;

    const [requestInProgress, setRequestInProgress] = useState(false);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(initialMessageTemplate, messageTemplateState);
    }, [initialMessageTemplate, messageTemplateState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-message-template-changes",
        modalType: ModalTypes.DiscardMessageTemplateChanges,
        stateIsUnchanged
    });

    useEffect(() => {
        setMessageTemplateState(initialMessageTemplate);
    }, [initialMessageTemplate])

    const validationErrors = useMemo(() => {
        return getMessageTemplateValidationErrors(messageTemplateState);
    }, [messageTemplateState]);

    const onCloseClick = () => {
        navigateToUtil(MESSAGE_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const renderConfirmSaveMessageTemplateModal = (
        confirmMessage: string | React.ReactNode,
        onSubmit: (messageTemplateDetailsState: FormattedMessageTemplate) => Promise<void>
    ) => {
        const renderModal = (onModalClose: () => void) => {
            return (
                <ConfirmModal
                    id={"confirm-save-message-template"}
                    title={resources.modals.updateMessageTemplate.title}
                    confirmMessage={confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resources.common.buttons.save.label}
                    onSubmit={null}
                    onSubmitAsync={onSubmit}
                    onModalClose={onModalClose}
                    customProps={messageTemplateState}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    };

    const onSubmit = async () => {
        setRequestInProgress(true);
        await handleSave(messageTemplateState);
        setRequestInProgress(false);
    };

    const confirmMessage = useMemo(() => {
        return (
            <div>
                <p>{resources.modals.updateMessageTemplate.confirmMessageTitle}</p>
                <p>{resources.modals.updateMessageTemplate.confirmMessageDescription}</p>
            </div>
        );
    }, []);

    const onClickSave = async () => {
        renderConfirmSaveMessageTemplateModal(confirmMessage, onSubmit);
    };

    return (
        <div className={styles.messageTemplateDetailsComponent}>
            <div className={styles.headerContainer}>
                <span className={styles.templateName}>
                    {`${messageTemplateState.name}`}
                </span>
                <div className={styles.buttonContainer}>
                    <Button
                        id="message-template-details-close-button"
                        onClick={onCloseClick}
                        variant={"outline-secondary"}
                    >
                        {resources.common.buttons.close.label}
                    </Button>
                    {initialMessageTemplate.permissions.canModify && (
                        <ButtonWithSpinner
                            id="message-template-detail-save"
                            disabled={stateIsUnchanged || requestInProgress || validationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                            label={resources.common.buttons.save.label}
                            type={ButtonTypes.SUBMIT}
                            onClick={onClickSave}
                            variant={"primary"}
                        />
                    )}
                </div>
            </div>
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="message-template-details" fill={true} unmountOnExit={true} onSelect={setCurrentTab}>
                    <Tab eventKey={MessageTemplateDetailsTabTypes.GENERAL_INFO} title={validationErrors.generalInfo.tabTitle}>
                        <MessageTemplateGeneralInfoTab
                            isDisabled={!initialMessageTemplate.permissions.canModify}
                            session={session}
                            axnCompanies={axnCompanies}
                            selfServiceVerificationWhiteLabeledEmails={selfServiceVerificationWhiteLabeledEmails}
                            state={messageTemplateState}
                            labelWidth={labelWidth}
                            validationErrors={validationErrors.generalInfo}
                            setMessageTemplateState={setMessageTemplateState}
                        />
                    </Tab>
                    <Tab eventKey={MessageTemplateDetailsTabTypes.CONTENT} title={validationErrors.contentAndVariables.tabTitle}>
                        <div>
                            <ContentPageHeader title={resource.tabs.content.title} />
                            <ContentPageBody isMaxWidth={true}>
                                <MessageTemplateContentTab
                                    isDisabled={!initialMessageTemplate.permissions.canModify}
                                    state={messageTemplateState}
                                    labelWidth={labelWidth}
                                    selectedLanguage={selectedLanguage}
                                    contentValidationErrors={validationErrors.content}
                                    variablesValidationErrors={validationErrors.variables}
                                    setSelectedLanguage={setSelectedLanguage}
                                    setMessageTemplateState={setMessageTemplateState}
                                />
                            </ContentPageBody>
                        </div>
                    </Tab>
                </Tabs>
            </TabContainer>
        </div>
    );
};